import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Link as RouterLink} from '@github-ui/react-router'
import {ActionList, ActionMenu, Button} from '@primer/react'
import type {ReactNode} from 'react'

import {ForYouItem} from '../shared/ForYouItem'
import type {ForYouItemAction} from '../shared/for-you-item.types'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsChangesRoute} from '../../../../routes/files-changed-routes'
import {useReviewRequestedData, type ReviewRequestedData} from './use-review-requested-data'
import {ReviewerIdentity} from '../../reviewers/ReviewerIdentity'
import {useOverviewBranchActions} from '../mergeability/shared/use-overview-branch-actions'

const ITEM_NAME = 'review-requested'

export function shouldShowReviewRequested(data: ReviewRequestedData | null) {
  return data !== null
}

// Surfaces the viewer's requested-review state in the "Needs attention" section, with a
// shortcut to start reviewing on the Files changed page.
export function ReviewRequested() {
  const {actions: branchActions, footer} = useOverviewBranchActions(ITEM_NAME)
  const {data, queryStatus} = useReviewRequestedData()
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  if (queryStatus !== 'success') {
    return null
  }

  if (!shouldShowReviewRequested(data)) {
    return null
  }

  const changesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})

  const actions = actionsFor(data, changesPath, sendPullRequestAnalyticsEvent)

  return (
    <ForYouItem
      itemId={ITEM_NAME}
      footer={footer}
      visual={{kind: 'icon', icon: ClockHandsIcon, tone: 'attention', glyphSize: 24}}
      title="Review requested"
      subText={subTextFor(data)}
      actions={[...actions, ...branchActions]}
    />
  )
}

function subTextFor(data: ReviewRequestedData): ReactNode {
  switch (data.kind) {
    case 'delegated':
      return data.delegatedFromTeams.length > 0 ? (
        <span>
          You have been assigned to review from{' '}
          {data.delegatedFromTeams.map((team, index) => (
            <span key={team.id.toString()}>
              {index > 0 && <span>, </span>}
              <ReviewerIdentity reviewer={team} withAvatar={false} inline inlineAccent />
            </span>
          ))}
        </span>
      ) : (
        'You have been assigned to review'
      )
    case 'codeowner':
      return 'Your review has been requested as a code owner'
    case 'direct':
      return 'Start your review of the changes.'
  }
}

function actionsFor(
  data: ReviewRequestedData,
  changesPath: string,
  sendPullRequestAnalyticsEvent: ReturnType<typeof usePullRequestAnalytics>['sendPullRequestAnalyticsEvent'],
): readonly ForYouItemAction[] {
  const sendMenuAction = (actionName: string) => {
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: ITEM_NAME,
      action_name: actionName,
    })
  }

  // Code owner: offer the full review plus a shortcut filtered to the viewer's own files. Use a
  // single-width menu button rather than two side-by-side buttons so the action always fits inside
  // the fixed-width trailing column and never clips at 320px / 400% zoom (WCAG 1.4.10 Reflow).
  if (data.kind === 'codeowner') {
    const viewerLogin = getCurrentUserLogin()
    const ownedFilesPath = viewerLogin ? `${changesPath}?owned-by[]=${encodeURIComponent(viewerLogin)}` : changesPath

    return [
      {
        id: 'review-menu',
        content: (
          <ActionMenu>
            <ActionMenu.Button size="small" onClick={() => sendMenuAction('open_review_menu')}>
              Start your review
            </ActionMenu.Button>
            <ActionMenu.Overlay align="end">
              <ActionList>
                <ActionList.LinkItem
                  as={RouterLink}
                  to={changesPath}
                  onClick={() => sendMenuAction('review_all_files')}
                >
                  Review all files
                </ActionList.LinkItem>
                <ActionList.LinkItem
                  as={RouterLink}
                  to={ownedFilesPath}
                  onClick={() => sendMenuAction('review_owned_files')}
                >
                  Review just your files
                </ActionList.LinkItem>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        ),
      },
    ]
  }

  return [
    {
      id: 'review',
      content: (
        <Button size="small" as={RouterLink} to={changesPath} onClick={() => sendMenuAction('review')}>
          Start your review
        </Button>
      ),
    },
  ]
}
